import React from 'react';
import { 
  Box, 
  IconButton, 
  Tooltip,
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Drawer 
} from '@mui/material';
import { 
  Menu as MenuIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getIconComponent } from '../../utils/iconUtils';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import { useProUpgrade } from '../../context/ProUpgradeContext';
import ProCrownIcon from '../billing/ProCrownIcon';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;
const layoutSurfaceColor = BRAND.surface;
const navActiveBackground = brandAlpha('accent', 0.12);
const navHoverBackground = brandAlpha('accent', 0.06);
const layoutHeaderHeight = 64;
const contentCornerRadius = 32;

const LeftSidebar = ({ 
  drawerOpen,
  useCompactNavigation,
  navigationItems, 
  isActivePath, 
  onDrawerToggle
}) => {
  const { openProUpgrade } = useProUpgrade();

  const getNavigationItemProps = (item, closeOnClick = false) => {
    const handleClick = (event) => {
      if (item.disabled) {
        return;
      }

      if (item.proLocked) {
        event?.preventDefault?.();
        openProUpgrade();
        if (closeOnClick && useCompactNavigation) {
          onDrawerToggle();
        }
        return;
      }

      item.onClick?.();

      if (closeOnClick && useCompactNavigation) {
        onDrawerToggle();
      }
    };

    return item.path && !item.proLocked
      ? {
          component: Link,
          to: item.path,
          onClick: handleClick
        }
      : {
          component: 'button',
          type: 'button',
          onClick: handleClick
        };
  };

  return (
    <>
      {/* Left Sidebar - Desktop */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          width: collapsedDrawerWidth,
          flexShrink: 0,
          flexDirection: 'column',
          alignSelf: 'stretch',
          overflow: 'hidden',
          bgcolor: layoutSurfaceColor,
          backgroundImage: 'none',
          color: BRAND.text,
          border: 'none',
          boxShadow: 'none'
        }}
      >
        {/* Logo/Brand Section */}
        <Box id="left-sidebar-logo-brand-section" sx={{ 
          px: 0,
          py: 0,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'transparent',
          minHeight: layoutHeaderHeight,
          height: layoutHeaderHeight,
          overflow: 'hidden',
          borderBottom: 'none',
          boxShadow: 'none'
        }} />

        {/* Navigation Menu */}
        <List sx={{ mt: 0, pt: `${contentCornerRadius}px`, overflowX: 'hidden' }}>
          {navigationItems.map((item) => {
            const IconComponent = getIconComponent(item.icon);
            const isActive = item.path ? isActivePath(item.path) : false;
            return (
              <Tooltip key={item.text} title={item.text} placement="right" arrow>
                <ListItem
                  {...getNavigationItemProps(item)}
                  sx={{ 
                    mx: 0,
                    mb: 0,
                    borderRadius: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    transition: 'all 0.2s ease-in-out',
                    minHeight: 48,
                    width: '100%',
                    overflow: 'visible',
                    bgcolor: isActive ? navActiveBackground : 'transparent',
                    textDecoration: 'none',
                    color: 'inherit',
                    px: 0,
                    border: 'none',
                    opacity: item.disabled ? 0.5 : 1,
                    pointerEvents: item.disabled ? 'none' : 'auto',
                    '&:hover': {
                      bgcolor: isActive ? navActiveBackground : navHoverBackground,
                      '& .MuiListItemIcon-root': {
                        color: BRAND.accentText
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: isActive ? BRAND.accentText : BRAND.textMuted,
                    minWidth: 0,
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <IconComponent />
                      {item.proLocked ? (
                        <Box sx={{ position: 'absolute', top: -8, right: -10, pointerEvents: 'none' }}>
                          <ProCrownIcon size={10} />
                        </Box>
                      ) : null}
                    </Box>
                  </ListItemIcon>
                  <ListItemText primary={item.text} sx={{ display: 'none' }} />
                </ListItem>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      {/* Mobile Drawer - Overlay */}
      <Drawer
        anchor="left"
        variant="temporary"
        open={drawerOpen && useCompactNavigation}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            bgcolor: layoutSurfaceColor,
            color: BRAND.text,
            border: 'none',
            borderRight: `1px solid ${BRAND.line}`,
            boxShadow: `4px 0 24px ${brandAlpha('primary', 0.12)}`,
            overflowX: 'hidden'
          }
        }}
      >
        {/* Logo/Brand Section */}
        <Box sx={{ 
          width: '100%',
          px: 2,
          py: 1,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end',
          bgcolor: layoutSurfaceColor,
          minHeight: layoutHeaderHeight,
          height: layoutHeaderHeight,
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <IconButton 
            onClick={onDrawerToggle}
            sx={{ 
              color: BRAND.textMuted,
              '&:hover': {
                bgcolor: 'transparent',
                color: BRAND.accentText
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Navigation Menu */}
        <List sx={{ mt: 0, pt: 0.5, overflowX: 'hidden' }}>
          {navigationItems.map((item) => {
            const IconComponent = getIconComponent(item.icon);
            const isActive = item.path ? isActivePath(item.path) : false;
            return (
              <ListItem
                key={item.text}
                {...getNavigationItemProps(item, true)}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: BRAND_RADIUS.button,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  transition: 'all 0.2s ease-in-out',
                  bgcolor: isActive ? navActiveBackground : 'transparent',
                  textDecoration: 'none',
                  color: 'inherit',
                  width: 'calc(100% - 16px)',
                  border: 'none',
                  opacity: item.disabled ? 0.5 : 1,
                  pointerEvents: item.disabled ? 'none' : 'auto',
                  '&:hover': {
                    bgcolor: isActive ? navActiveBackground : navHoverBackground,
                    '& .MuiListItemIcon-root': {
                      color: BRAND.accentText
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  color: isActive ? BRAND.accentText : BRAND.textMuted,
                  minWidth: 40,
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <IconComponent />
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  sx={{ 
                    '& .MuiTypography-root': { 
                      fontSize: '0.9rem',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? BRAND.accentText : BRAND.text
                    }
                  }}
                />
                {item.proLocked ? <Box sx={{ ml: 0.5, display: 'flex' }}><ProCrownIcon size={11} /></Box> : null}
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </>
  );
};

export default LeftSidebar;
